import { useEffect, useRef, useState } from 'react';
import { parsePartialJson } from 'ai';

import { streamAiStory } from 'utils/api/ai-stories.api';

const REQUIRED_CARD_FIELDS = [
    'id',
    'card_type',
    'group',
    'label',
    'value',
    'sub',
    'cta_text',
    'prompt',
];

/**
 * Extract complete card objects from an in-progress JSON buffer of shape
 * `{"insights": [{...}, {...}, ...]}`.
 *
 * `parsePartialJson` (from `ai`) parses the longest repairable prefix of the
 * buffer and tells us via `state` whether the JSON is fully closed
 * (`successful-parse`) or had to be repaired (`repaired-parse`). On a repair,
 * the trailing array element is likely mid-stream — we drop it so a
 * mid-value truncation never reaches the UI. The final `payload` SSE event
 * delivers the canonical list at end-of-stream, so the last card always
 * lands correctly even after we hold it back here.
 */
const extractCompleteCards = async (buffer) => {
    const { value, state } = await parsePartialJson(buffer);
    if (state === 'failed-parse' || state === 'undefined-input') return [];
    const cards = value?.insights;
    if (!Array.isArray(cards) || cards.length === 0) return [];
    const safeCards = state === 'successful-parse' ? cards : cards.slice(0, -1);
    return safeCards
        .filter((card) =>
            REQUIRED_CARD_FIELDS.every(
                (k) => typeof card[k] === 'string' && card[k].length > 0,
            ),
        )
        .map(({ metric: _metric, ...rest }) => rest);
};

/**
 * Fetch the entity-page insight cards from /ai/stories/.
 *
 * The backend streams text deltas of the LLM's structured JSON output. As
 * each card object closes inside the streamed JSON, we surface it — the user
 * sees cards appear progressively instead of waiting for the full response.
 * A final `payload` event arrives once the LLM completes, which we use as
 * the source-of-truth in case partial parsing missed anything.
 */
export const useEntityInsight = ({
    entityId,
    entityType,
    entityRoute,
    country,
    dataSource = 'pop_index',
    week,
}) => {
    const [insights, setInsights] = useState([]);
    // Start loading immediately so the skeleton shows from first render — the
    // effect below waits for `country` and `week` to resolve before actually
    // hitting the API, but the UI doesn't have to wait for that.
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const controllerRef = useRef(null);

    // Accumulating buffer of streamed JSON text. Lives in a ref because we
    // update it on every delta and don't want to trigger re-renders for the
    // raw string — only for the parsed cards.
    const bufferRef = useRef('');

    useEffect(() => {
        // Wait for all required deps to resolve before firing — `country` and
        // `week` start undefined and resolve a beat later, and firing during
        // that window costs an extra (wasted) LLM call. Skeleton stays up.
        if (!entityId || !entityType || !country || !week) return undefined;

        bufferRef.current = '';
        setInsights([]);
        setError(null);
        setIsLoading(true);

        controllerRef.current = streamAiStory(
            {
                entityId,
                entityType,
                country,
                dataSource,
                week,
                entityRoute,
            },
            {
                onDelta: async ({ text }) => {
                    if (typeof text !== 'string') return;
                    bufferRef.current += text;
                    const cards = await extractCompleteCards(bufferRef.current);
                    // Monotonic update — `parsePartialJson` is async, so two
                    // deltas could resolve out of order. Only accept results
                    // that surface more cards than we already show; a stale
                    // resolution with fewer cards is dropped.
                    setInsights((prev) =>
                        cards.length > prev.length ? cards : prev,
                    );
                },
                onPayload: ({ insights: ins }) => {
                    if (Array.isArray(ins)) setInsights(ins);
                },
                onComplete: () => {
                    setIsLoading(false);
                },
                onError: (err) => {
                    console.error('[EntityInsightBar] stream error:', err);
                    setError(err);
                    setIsLoading(false);
                },
            },
        );

        return () => {
            controllerRef.current?.abort();
        };
    }, [entityId, entityType, country, dataSource, week, entityRoute]);

    return { insights, isLoading, error };
};
